import { useEffect, useRef, useState } from 'react'
import { NavLink } from 'react-router-dom'
import { Tooltip } from '@nazka/nazka.mapframe.components'

import { useTranslation } from '@utils/i18n/i18n.consumers';
import { useOnClickOutside } from '@services/hooks.service';

import './definition-component.styl'

function DefinitionComponent({
  id,
  term,
  definition,
  learnMoreUrl,
  className,
  lowercase = false,
}) {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef(null)

  useOnClickOutside(triggerRef, () => setOpen(false));

  useEffect(() => {
    if (!open) {
      return undefined
    }

    const el = triggerRef.current
    if (!el) {
      return undefined
    }

    const getScrollParent = (node) => {
      let current = node?.parentElement
      while (current) {
        const style = window.getComputedStyle(current)
        const overflow = style.overflow + style.overflowX + style.overflowY
        if (/(auto|scroll)/.test(overflow)) {
          return current
        }
        current = current.parentElement
      }
      return null
    }

    const root = getScrollParent(el)

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) {
          setOpen(false)
        }
      },
      { root, threshold: 0 },
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [open])

  const { t } = useTranslation();

  const isValid = Boolean(id && term && definition)

  const tooltipId = `definition-tooltip-${id}`

  if (!isValid) {
    return null;
  }

  return (
    <Tooltip
      id={tooltipId}
      isOpen={open}
      openOnClick
      clickable
      noArrow
      place="top"
      className="definition-component__tooltip"
      tooltip={(
        <div
          className="definition-component__tooltip__content"
          onMouseDown={e => e.stopPropagation()}
          onTouchStart={e => e.stopPropagation()}
        >
          <div className="definition-component__tooltip__term">{t(term)}</div>
          <div className="definition-component__tooltip__definition">{t(definition)}</div>
          {learnMoreUrl && (
            <NavLink
              className="definition-component__tooltip__learn-more"
              to={learnMoreUrl}
              onClick={() => setOpen(false)}
            >
              {t('Learn more')}
            </NavLink>
          )}
        </div>
      )}
    >
      <span ref={triggerRef} className="definition-component" data-tooltip-id={tooltipId}>
        <button
          type="button"
          className={`definition-component__trigger${className ? ` ${className}` : ''}`}
          aria-expanded={open}
          aria-controls={tooltipId}
          onClick={() => setOpen(prev => !prev)}
        >
          {lowercase ? t(term).toLowerCase() : t(term)}
        </button>
      </span>
    </Tooltip>
  )
}

export default DefinitionComponent
